/**
 * РЕЗЕРВ РАМЫ СУЖАЕТСЯ ДО ТОГО, ЧТО ОНА РЕАЛЬНО ЗАКРЫВАЕТ.
 *
 * ЧТО БЫЛО ИЗМЕРЕНО.
 *
 * Полоса кадра — прямоугольник, и любой прямоугольник хрома она берёт как
 * вставку от края НА ВСЮ перпендикулярную сторону. Для ленты этапов это верно:
 * лента тянется поперёк холста, из-под неё вбок не уйти. Для орбит — нет.
 * Замер на живом стенде 1440×876: орбита 152×531, а резервируется полоса
 * 170 px шириной на ВСЮ высоту холста — 876 px. Орбита закрывает 531 из 876,
 * то есть 39,4 % зарезервированной полосы не закрыто ничем.
 *
 * Цена этого резерва названа числом: на широкой квартире ширинный член кадра
 * доминирует, и два резерва по 170 px растягивают кадр в
 * 1440 / (1440 − 340) = 1,309 раза. Модель линейно ужимается до 76,4 %, по
 * площади — до 58,4 %. Плата за защиту, которая в этом состоянии не нужна
 * вовсе: модель до орбит не доставала.
 *
 * ПОЧЕМУ ПЕРЕБОР, А НЕ ПРАВИЛО.
 *
 * Резерв решается ПО ОСИ: вертикальные вставки берутся обе или не берутся
 * вовсе, горизонтальные — так же. Четыре варианта, и каждый можно ПРИМЕНИТЬ и
 * ПОСМОТРЕТЬ, а не вывести: применить кадр, спроецировать коробку, спросить,
 * заезжает ли модель хоть на один прямоугольник рамы. Годные варианты
 * сравниваются по площади модели на экране, негодные отбрасываются.
 *
 * ПОЧЕМУ ПО ОСИ, А НЕ ПО КАЖДОЙ СТОРОНЕ ОТДЕЛЬНО.
 *
 * Потому что это было написано по стороне и замерено. Перебор из 16 вариантов
 * на шкафу 1024×744 выбирал «снять правый резерв, левый оставить»: в
 * перспективе косой кадр давал на 2,1 % больше площади (101 842 против
 * 99 704 px²). Плата — модель уезжала на 38 px вправо от середины симметричной
 * оболочки. Два процента площади не стоят кадра, который выглядит сбитым, и
 * решение «сдвинуть картинку вбок» — это решение о ВИДЕ, а его принимает не
 * код. Снятая пара оставляет полосу там же, где она была.
 *
 * Правило «снимать вставку, если зона её не закрывает» пробовалось первым и
 * промахнулось дважды, оба раза на замере:
 *
 *  · снятый резерв не только УВЕЛИЧИВАЕТ модель, но и ПЕРЕЦЕНТРИРУЕТ полосу.
 *    Проба «без резерва вообще» оставляла шкаф на 2 px ниже кромки навигации
 *    (top 74 против 72), а кадр, применённый следом по одной ленте, поднимал
 *    его на 53 — под остров. Проверять надо ТО, ЧТО ПРИМЕНЕНО, а не пробу.
 *
 *  · снимать вставки по одной нельзя ещё и по геометрии: убрав только левую,
 *    полоса съезжает влево, и модель идёт К ТОЙ САМОЙ орбите, от которой
 *    отступали. Отсюда и решение по оси.
 *
 * Перебор стоит пять вписываний, каждое — восемь скалярных произведений и одна
 * проекция. Он случается только при явном вписывании (загрузка бандла, смена
 * проекции, пересчёт на смену рамы), а не в цикле отрисовки.
 *
 * ЧЕГО ЗДЕСЬ НЕТ.
 *
 * Здесь нет размена «пусть немного заедет под хром, зато будет крупнее».
 * Вариант, в котором модель хоть чем-то заезжает на раму, отбрасывается
 * ЦЕЛИКОМ, а не штрафуется. Владелец сказал про шкаф «нижнее меню перекрывает
 * шкаф», а не «перекрывает, но зато крупно»; решение о виде принимает он.
 */
import type { FrameInsets, ReadRect } from './viewportInsets';
import { insetsFromRects, rectsOverlap } from './viewportInsets';

/**
 * Экранный прямоугольник модели ПОСЛЕ применённого кадра.
 *
 * В той же системе отсчёта, что и прямоугольники зон, — в координатах окна,
 * чтобы сравнивать их напрямую, без второго перевода координат, в котором и
 * живут ошибки на пиксель.
 */
export type ProjectedModelRect = ReadRect;

/**
 * Четыре варианта резерва, от самого лёгкого к самому осторожному.
 * Порядок — часть правила: при равной площади побеждает первый.
 */
const RESERVE_CHOICES: readonly Readonly<{ vertical: boolean; horizontal: boolean }>[] = [
  { vertical: false, horizontal: false },
  { vertical: true, horizontal: false },
  { vertical: false, horizontal: true },
  { vertical: true, horizontal: true },
];

/**
 * НАСКОЛЬКО ДВА КАДРА СЧИТАЮТСЯ ОДИНАКОВЫМИ ПО ПЛОЩАДИ.
 *
 * Перспектива добавляет к площади десятые доли процента от того, куда уведена
 * цель, и без допуска выбор скакал бы на этом шуме. Варианты перебираются от
 * меньшего резерва к большему, поэтому при равенстве побеждает первый — тот,
 * что не тратит лишних вставок.
 */
const EQUAL_AREA_TOLERANCE = 1.005;

/**
 * Выбрать вставки, которые рама действительно должна стоить.
 *
 * @param rects     прямоугольники постоянной рамы — нарисованной и обещанной.
 * @param canvasRect холст как система отсчёта.
 * @param apply     применить кадр по заданным вставкам и ВЕРНУТЬ экранный
 *                  прямоугольник модели. Это замер применённого кадра, а не
 *                  его предсказание: считать вторым способом то, что уже
 *                  посчитала камера, значит завести второй источник истины.
 * @returns вставки, по которым применён ПОСЛЕДНИЙ кадр.
 */
export function narrowFrameReserve(
  rects: readonly ReadRect[],
  canvasRect: ReadRect,
  apply: (insets: FrameInsets) => ProjectedModelRect | null,
): FrameInsets {
  const full = insetsFromRects(rects, canvasRect);
  if (rects.length === 0) {
    apply(full);
    return full;
  }

  let best: { insets: FrameInsets; area: number } | null = null;
  for (const choice of RESERVE_CHOICES) {
    const insets: FrameInsets = {
      top: choice.vertical ? full.top : 0,
      bottom: choice.vertical ? full.bottom : 0,
      left: choice.horizontal ? full.left : 0,
      right: choice.horizontal ? full.right : 0,
    };
    const model = apply(insets);
    if (model === null) continue;
    // Заезд на раму отбрасывает вариант целиком: закрытая рамой часть модели —
    // это тот самый дефект, ради которого весь слой и появился.
    if (rects.some((rect) => rectsOverlap(rect, model))) continue;
    const area = model.width * model.height;
    // «Заметно больше», а не «больше»: при равенстве остаётся ПЕРВЫЙ найденный,
    // а первым идёт вариант с меньшим резервом — равный кадр не должен стоить
    // резерва.
    if (best === null || area > best.area * EQUAL_AREA_TOLERANCE) best = { insets, area };
  }

  const chosen = best?.insets ?? full;
  apply(chosen);
  return chosen;
}
